import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { useState } from "react";
import { subscribeNewsletter } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_TEL, BUSINESS_ADDRESS } from "@/lib/whatsapp";
import { SITE_URL, SITE_HOST } from "@/lib/site";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed! Check your inbox for our welcome guide.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-forest text-white mt-24">
      <div className="container-x py-16 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Weight Loss Gurukul"
              className="h-14 w-auto object-contain bg-sand-100 rounded-xl p-1.5"
            />
            <span className="font-serif text-2xl">
              Weight Loss <span className="italic text-clay-light">Gurukul</span>
            </span>
          </div>
          <p className="mt-5 text-white/75 max-w-md leading-relaxed">
            A proven, beginner-friendly system — science-backed and habit-driven, designed to help you: burn fat, look toned and athletic, maximise energy and build sky-high confidence.
            <br />
            1-on-1 personalised live coaching - from the comfort of your home.
          </p>

          <form onSubmit={handleSubscribe} className="mt-8 max-w-md">
            <label className="eyebrow text-white/60">Join the newsletter</label>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 rounded-full bg-white/10 border border-white/15 px-5 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-clay"
              />
              <button
                data-testid="newsletter-subscribe-btn"
                type="submit"
                disabled={loading}
                className="rounded-full bg-clay px-5 py-3 text-sm font-medium hover:bg-clay-dark disabled:opacity-60"
              >
                {loading ? "…" : "Subscribe"}
              </button>
            </div>
          </form>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-4">Explore</h4>
          <ul className="space-y-2 text-sm text-white/75">
            <li><Link to="/programs" className="hover:text-clay-light">Programs</Link></li>
            <li><Link to="/about" className="hover:text-clay-light">About the Guru</Link></li>
            <li><Link to="/blog" className="hover:text-clay-light">Blog</Link></li>
            <li><a href="#schedule" className="hover:text-clay-light">Class Schedule</a></li>
            <li><a href="#faq" className="hover:text-clay-light">FAQs</a></li>
          </ul>

          <h4 className="font-serif text-lg mb-4 mt-8">Legal</h4>
          <ul className="space-y-2 text-sm text-white/75">
            <li><Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-clay-light">Privacy Policy</Link></li>
            <li><Link to="/terms" data-testid="footer-link-terms" className="hover:text-clay-light">Terms & Conditions</Link></li>
            <li><Link to="/refund" data-testid="footer-link-refund" className="hover:text-clay-light">Refund Policy</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-4">Connect</h4>
          <ul className="space-y-3 text-sm text-white/75">
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 shrink-0" />
              <a href="mailto:hello@weightlossgurukul.com" className="hover:text-clay-light">hello@weightlossgurukul.com</a>
            </li>
            <li className="flex items-start gap-2">
              <Phone className="h-4 w-4 mt-0.5 shrink-0" />
              <a href={`tel:${PHONE_TEL}`} className="hover:text-clay-light">{PHONE_DISPLAY}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
              <address className="not-italic leading-relaxed">
                {BUSINESS_ADDRESS.line1}<br />
                {BUSINESS_ADDRESS.line2}<br />
                {BUSINESS_ADDRESS.line3}<br />
                {BUSINESS_ADDRESS.line4}
              </address>
            </li>
          </ul>
          <div className="mt-5 flex gap-3">
            <a href="#" className="p-2 rounded-full bg-white/10 hover:bg-clay transition-colors" aria-label="Instagram"><Instagram className="h-4 w-4" /></a>
            <a href="#" className="p-2 rounded-full bg-white/10 hover:bg-clay transition-colors" aria-label="YouTube"><Youtube className="h-4 w-4" /></a>
            <a href="#" className="p-2 rounded-full bg-white/10 hover:bg-clay transition-colors" aria-label="Facebook"><Facebook className="h-4 w-4" /></a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} Weight Loss Gurukul. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link to="/privacy" className="hover:text-clay-light">Privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="hover:text-clay-light">Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/refund" className="hover:text-clay-light">Refund</Link>
            <span aria-hidden="true">·</span>
            <a href={SITE_URL} className="hover:text-clay-light">{SITE_HOST}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
